<template>
  <q-btn
    v-if="isEnabled"
    :color="color"
    :disable="disable"
    :loading="loading"
    :icon="icon"
    :label="label"
    :flat="flat"
    :round="round"
    :dense="dense"
    :fab="fab"
    :unelevated="unelevated"
    :stretch="stretch"
    :size="size"
    :no-wrap="noWrap"
    :text-color="textColor"
    :title="title"
    :style="style"
    v-bind="$attrs"
  >
    <slot />
  </q-btn>
  <CuiButton
    v-else
    :disabled="disable"
    :loading="loading"
    :size="normalizedSize"
    :ariaLabel="title || label || ariaLabel || icon"
    :iconOnly="!hasTextContent"
    :variant="fab ? 'hero' : 'secondary-outline'"
    v-bind="$attrs"
  >
    <template v-if="normalizedIcon" #icon>
      <span class="material-symbols-rounded">{{ normalizedIcon }}</span>
    </template>
    {{ label }}
    <slot />
  </CuiButton>
</template>

<script setup lang="ts">
import { computed, useSlots } from "vue";
import { CuiButton } from "@ist/commonui-components";
import { useFeatureFlags } from "@/composables/useFeatureFlags";

const { isEnabled: checkEnabled } = useFeatureFlags();
const isEnabled = checkEnabled("dtpBtn");
const slots = useSlots();
type Props = {
  color?: string;
  disable?: boolean;
  loading?: boolean;
  icon?: string;
  label?: string;
  flat?: boolean;
  round?: boolean;
  dense?: boolean;
  fab?: boolean;
  unelevated?: boolean;
  stretch?: boolean;
  size?: string;
  noWrap?: boolean;
  textColor?: string;
  title?: string;
  ariaLabel?: string;
  style?: string | Record<string, any>;
};

const props = withDefaults(defineProps<Props>(), {
  color: undefined,
  disable: false,
  loading: false,
  icon: undefined,
  label: undefined,
  flat: false,
  round: false,
  dense: false,
  fab: false,
  unelevated: false,
  stretch: false,
  size: undefined,
  noWrap: false,
  textColor: undefined,
  title: undefined,
  style: undefined
});

const hasTextContent = computed(() => {
  // Check if label prop has text
  if (props.label && props.label.trim()) return true;

  // Check if default slot has text content
  if (slots.default) {
    const slotContent = slots.default();
    return slotContent.some(vnode => {
      // Check for text nodes
      if (typeof vnode.children === "string" && vnode.children.trim()) return true;
      // Check for array of children (nested content)
      if (Array.isArray(vnode.children)) {
        return vnode.children.some(child =>
          typeof child === "string" && child.trim()
        );
      }
      return false;
    });
  }

  return false;
});

const normalizedSize = computed(() => {
  // Map Quasar button sizes to CuiButton sizes
  const sizeMap: Record<string, "small" | "medium" | "large"> = {
    "xs": "small",
    "sm": "small",
    "md": "medium",
    "lg": "large",
    "xl": "large"
  };

  if (props.size) return sizeMap[props.size];
  // If dense is true and size is not set, default to small
  if (props.dense) return "small";
  return undefined;
});

const normalizedIcon = computed(() => {
  if (!props.icon) return undefined;
  // Remove mdi- prefix if present
  const iconName = props.icon.startsWith("mdi-") ? props.icon.slice(4) : props.icon;

  // Map specific icon names to Material Symbols equivalents
  const iconMap: Record<string, string> = {
    "message-alert": "chat_info",
    "printer": "print",
    "printer-off": "print_off",
    "printer-alert": "print_alert",
    "printer-error": "print_error",
    "printer-warning": "print_warning",
    "printer-info": "print_info",
    "printer-success": "print_success",
    "printer-disabled": "print_disabled",
    "bell": "notifications",
    "account-off": "person_off",
    "pencil": "edit",
    "undo-variant": "undo",
    "comment-plus": "add_comment",
    "comment-text": "chat"
  };

  return iconMap[iconName] || iconName;
});
</script>
